import {ChangeDetectorRef, Directive, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FUIAbstractInput} from "../../framework/abstract-input";
import {Subscription} from "rxjs";
import {CalendarComponent} from "@progress/kendo-angular-dateinputs";
import {FUIEventsService} from "../../../events/events.service";

@Directive()
export abstract class FUIAbstractDateInput extends FUIAbstractInput implements OnDestroy, OnInit {

    private _calendar: CalendarComponent;
    private calendarFocusPending: boolean = false;
    private _$click: Subscription;
    public popped: boolean;
    private toggling = false;

    constructor(public elementRef: ElementRef,
                changeDetectorRef: ChangeDetectorRef,
                protected eventsService: FUIEventsService) {
        super(changeDetectorRef);
    }

    ngOnInit(): void {
        this._$click = this.eventsService.click$.subscribe(
            (click) => {
                if (!this.toggling && this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._$click.unsubscribe();
    }

    @ViewChild('calendar')
    set calendar(value: CalendarComponent) {
        this._calendar = value;
        if (this.calendarFocusPending) {
            if (this._calendar) {
                this._calendar.focus();
            }
            this.calendarFocusPending = false;
        }
    }

    get calendar(): CalendarComponent {
        return this._calendar;
    }

    /**
     *
     * @param {boolean} popped
     */
    public pop(popped: boolean): void {
        this.popped = popped;
    }

    /**
     * Perform events after a click on the host
     * @param element
     */
    @HostListener("click")
    override click(): void {
        this.focus();
        this.popped = false;
    }

    toggle(event?: Event) {
        if (!this.disabled && !this.toggling) {
            this.toggling = true;
            setTimeout(() => this.toggling = false);
            this.popped = !this.popped;
            if (this.popped) {
                setTimeout(() => {
                    if (this._calendar) {
                        this._calendar.focus();
                    } else {
                        this.calendarFocusPending = true;
                    }
                });
            } else {
                this.focus();
            }
            if (event) {
                event.stopPropagation();
            }
        }
    }

    /**
     * If the user presses enter (while not popped) or space, toggle the popup.
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keypress", ["$event"])
    keypress(keyboardEvent: KeyboardEvent) {
        if (!this.disabled) {
            if (((keyboardEvent.code === "Enter" || keyboardEvent.key === "Enter") && !this.popped) ||
                (keyboardEvent.code === "Space" || keyboardEvent.key === " ")) {
                this.toggle(keyboardEvent);
                keyboardEvent.preventDefault();
            }
        }
    }

    /**
     * If the user presses escape (while popped), toggle the popup.
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keydown", ["$event"])
    keydown(keyboardEvent: KeyboardEvent) {
        if (!this.disabled) {
            if ((keyboardEvent.code === "Escape" || keyboardEvent.key === "Escape") && this.popped) {
                this.toggle(keyboardEvent);
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    override onBlur(event?: Event): void {
        super.onBlur(event);

        // The kendo date input by default doesn't allow selection of a date before 1/1/1900,
        // however it doesn't apply that validation to dates entered as text.
        if (this.value instanceof Date && this.value.getFullYear() < 1900) {
            let newValue: Date = new Date(this.value.getTime());
            newValue.setFullYear(1900);
            this.value = newValue;
        }
    }

    popClicked(event?: Event) {
        event.stopPropagation();
    }

    calendarChanged(value: Date) {
        this.toggle();
    }
}
